import { Component, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionsSubject, Store } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { AuthService } from "../auth/auth.service";
import { GoogleIntegrationComponent } from "./google-integration/google-integration.component";
import { MicrosoftIntegrationComponent } from "./microsoft-integration/microsoft-integration.component";
import { CalamariIntegrationComponent } from "./calamari-integration/calamari-integration.component";
import { BillingSettingsService } from "../billing/settings/billing-settings.service";
import { SyncProgressService } from "../sync-progress/sync-progress.service";
import {
  BillingSettings,
  IntegrationSettings,
} from "../settings/settings.interface";
import {
  GET_INTEGRATION_SETTINGS_FAILURE,
  GET_INTEGRATION_SETTINGS_SUCCESS,
  GetIntegrationSettingsAction,
} from "../settings/settings.actions";
import { getSettingsSelector } from "../settings/settings.reducer";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-integration-settings",
  templateUrl: "./integration-settings.component.html",
  styleUrls: ["./integration-settings.component.scss"],
  standalone: false,
})
export class IntegrationSettingsComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleIntegrationComponent)
  googleIntegration!: GoogleIntegrationComponent;
  @ViewChild(MicrosoftIntegrationComponent)
  microsoftIntegration!: MicrosoftIntegrationComponent;
  @ViewChild(CalamariIntegrationComponent)
  calamariIntegration!: CalamariIntegrationComponent;

  public googleExpanded = true;
  public microsoftExpanded = false;
  public calamariExpanded = false;
  public selectedTabIndex = 0;

  public googleAbsenceEnabled = false;
  public microsoftAbsenceEnabled = false;
  public calamariAbsenceEnabled = false;

  public billingSettings: BillingSettings | null = null;
  public activeSyncs: {
    googleSyncStateId?: string;
    microsoftSyncStateId?: string;
    calamariSyncStateId?: string;
  } = {};
  public loading = true;

  private settingsReady = false;
  private billingReady = false;
  private syncsReady = false;
  private destroy$ = new Subject<void>();

  get isAbsenceEnabled(): boolean {
    return (
      this.googleAbsenceEnabled ||
      this.microsoftAbsenceEnabled ||
      this.calamariAbsenceEnabled
    );
  }

  constructor(
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly billingSettingsService: BillingSettingsService,
    private readonly syncProgressService: SyncProgressService,
    private readonly store: Store,
    private readonly actions$: ActionsSubject,
  ) {}

  ngOnInit(): void {
    const appUser = this.auth.appUser;
    const fromStart = sessionStorage.getItem("fromStartPage");

    if (fromStart && appUser?.provider) {
      this.setProviderTab(appUser.provider);
    }
    sessionStorage.removeItem("fromStartPage");

    this.store.dispatch(new GetIntegrationSettingsAction());
    this.store
      .select(getSettingsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: IntegrationSettings | null) => {
        if (!settings) {
          return;
        }
        this.settingsReady = true;
        this.loading = !(
          this.settingsReady &&
          this.billingReady &&
          this.syncsReady
        );
      });

    this.actions$
      .pipe(
        ofType(
          GET_INTEGRATION_SETTINGS_SUCCESS,
          GET_INTEGRATION_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.settingsReady = true;
        this.loading = !(
          this.settingsReady &&
          this.billingReady &&
          this.syncsReady
        );
      });

    this.billingSettingsService
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.billingSettings = settings;
          this.billingReady = true;
          this.loading = !(
            this.settingsReady &&
            this.billingReady &&
            this.syncsReady
          );
        },
        error: () => {
          this.billingSettings = null;
          this.billingReady = true;
          this.loading = !(
            this.settingsReady &&
            this.billingReady &&
            this.syncsReady
          );
        },
      });

    this.syncProgressService
      .checkActiveSyncsInBackend()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (activeSyncs) => {
          this.activeSyncs = activeSyncs;
          this.syncsReady = true;
          this.loading = !(
            this.settingsReady &&
            this.billingReady &&
            this.syncsReady
          );
        },
        error: () => {
          this.activeSyncs = {};
          this.syncsReady = true;
          this.loading = !(
            this.settingsReady &&
            this.billingReady &&
            this.syncsReady
          );
        },
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const syncParam = params.sync as string;
        this.handleSyncParameter(syncParam);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.googleExpanded = index === 0;
    this.microsoftExpanded = index === 1;
    this.calamariExpanded = index === 2;
  }

  private setProviderTab(provider: string): void {
    if (provider === "MICROSOFT") {
      this.onTabChange(1);
      return;
    }
    if (provider === "CALAMARI") {
      this.onTabChange(2);
      return;
    }
    this.onTabChange(0);
  }

  private handleSyncParameter(syncParam: string): void {
    if (syncParam === "google") {
      this.onTabChange(0);
      setTimeout(() => {
        this.googleIntegration?.syncGoogle();
        this.removeSyncParameter();
      }, 100);
    } else if (syncParam === "microsoft") {
      this.onTabChange(1);
      setTimeout(() => {
        this.microsoftIntegration?.syncMicrosoft();
        this.removeSyncParameter();
      }, 100);
    } else if (syncParam === "auto") {
      const provider = this.auth.appUser?.provider;
      if (provider === "GOOGLE") {
        this.onTabChange(0);
        setTimeout(() => {
          this.googleIntegration?.syncGoogle();
          this.removeSyncParameter();
        }, 100);
      } else if (provider === "MICROSOFT") {
        this.onTabChange(1);
        setTimeout(() => {
          this.microsoftIntegration?.syncMicrosoft();
          this.removeSyncParameter();
        }, 100);
      }
    }
  }

  private removeSyncParameter(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sync: null },
      queryParamsHandling: "merge",
    });
  }
}
